<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from 'vue';
import { NxButton } from '../Button';
import { NxDialog } from '../Dialog';
import { NxTextInput } from '../Inputs';
import { i18nInjectionKey } from '../internal';

const isOpen = defineModel<boolean>('open', { required: true });
const props = defineProps<{ max: number }>();
const emit = defineEmits<{ submit: [position: number] }>();

const i18n = inject(i18nInjectionKey)!;
const raw = ref('');

watch(isOpen, (open) => {
  if (open) raw.value = '';
});

const parsed = computed(() => {
  const n = Number(String(raw.value).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
});

const canSubmit = computed(() => {
  const n = parsed.value;
  return n != null && n >= 1 && n <= props.max;
});

function submit(close?: () => void) {
  if (!canSubmit.value || parsed.value == null) return;
  emit('submit', Math.round(parsed.value));
  if (close) close();
  else isOpen.value = false;
}

const inputRef = ref<{ inputElement?: HTMLInputElement } | null>(null);
watch(isOpen, async (open) => {
  if (!open) return;
  await nextTick();
  inputRef.value?.inputElement?.focus();
  inputRef.value?.inputElement?.select();
});
</script>

<template>
  <NxDialog
    v-model:open="isOpen"
    :title="i18n.t('common.table.move_to_position.title')"
    :description="i18n.t('common.table.move_to_position.description', { max: props.max })"
    :resizable="false"
  >
    <template #content>
      <div class="move-pos-body">
        <NxTextInput
          ref="inputRef"
          v-model="raw"
          inputmode="numeric"
          :placeholder="i18n.t('common.table.move_to_position.placeholder')"
          height="s"
          @keydown.enter="submit()"
        />
      </div>
    </template>
    <template #buttons="{ close }">
      <NxButton :disabled="!canSubmit" @click="() => submit(close)">
        {{ i18n.t('common.table.move_to_position.confirm') }}
      </NxButton>
      <NxButton variant="outline" @click="close">
        {{ i18n.t('common.table.move_to_position.cancel') }}
      </NxButton>
    </template>
  </NxDialog>
</template>

<style scoped>
.move-pos-body {
  width: min(var(--nxt-panel-width-lg), 92vw);
}
</style>
